/**
 * Dialog for creating new shifts
 * Includes form validation, conflict detection, and employee/group selection
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react';
import { useDebounce } from '@/hooks/useDebounce';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { TimePicker } from '@/components/ui/time-picker';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { DatePicker } from '@/components/ui/date-picker';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';
import { focusFirstShiftField } from './dialogFocus';
import {
  PeopleCollisionAlert,
  ShiftCollisionAlert,
  collisionFromError,
  hasCollision,
  peopleCollisionsFromError,
  type PersonCollision,
  type ShiftCollision,
} from './ShiftCollisionAlert';
import { availabilityLine } from './availabilityLines';
import { formatDayList } from '@/hooks/shiftSeriesToast';
import { expandRecurrenceDates } from './recurrenceDates';
import { formatDateDisplay } from '@/lib/format';
import { EmployeeChipSelect, type EmployeeMarker } from './EmployeeChipSelect';
import { GroupMultiSelect } from './GroupMultiSelect';
import { useShiftWriteScope } from '@/hooks/useShiftWriteScope';
import { WeekdayMultiSelect } from './WeekdayMultiSelect';
import { ShiftTypeBadge } from './ShiftTypeBadge';
import { ShiftAvailabilityWarning } from './ShiftAvailabilityWarning';
import { useCreateShift, useCheckShiftConflicts } from '@/hooks/useShifts';
import { useShiftTemplates } from '@/hooks/useShiftTemplates';
import { useEmployeeAbsences } from '@/hooks/useEmployeeAbsences';
import { useAllEmployees } from '@/hooks/useEmployees';
import { orderTemplatesByRecentUse, readRecentTemplateIds, rememberTemplateUse } from './templateUsage';
import { getAbsenceTypeLabel } from '@/components/absences/AbsenceTypeBadge';
import { ShiftType } from '@/types/shifts';
import { shifts_CheckConflictsRequest } from '@/api/models/shifts_CheckConflictsRequest';
import { toEnumMember } from '@/lib/enumMember';
import { ShiftWarningLines, type NamedShiftWarnings } from './ShiftWarningLines';
import { ApprovalStatus, type AbsenceTypeValue } from '@/types/employeeAbsences';
import {
  getCreateShiftSchema,
  toCreateShiftRequest,
  RecurrenceType,
  OPEN_SHIFT_VALUE,
  type CreateShiftFormData,

  type CreateShiftFormInput,
} from '@/schemas/shiftSchemas';

export interface CreateShiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDate?: Date;
  defaultEmployeeId?: string;
}

/**
 * Dialog component for creating new shifts.
 * Integrates with the useCreateShift and useCheckShiftConflicts hooks.
 */
export function CreateShiftDialog({
  open,
  onOpenChange,
  defaultDate,
  defaultEmployeeId,
}: CreateShiftDialogProps) {
  const { i18n } = useLingui();
  const { data: employeeList } = useAllEmployees();
  // First names for the toast, the warning lines and the collision lists (#2121); the full
  // name when two people of the list share a first name.
  const firstNameOf = useCallback(
    (employeeId: string) => {
      const employee = employeeList?.find((e) => e.id === employeeId);
      const first = employee?.firstname?.trim() || '';
      const shared =
        !!first &&
        (employeeList ?? []).filter((e) => e.id !== employeeId && e.firstname?.trim() === first).length > 0;
      const full = `${first} ${employee?.lastname?.trim() || ''}`.trim();
      return shared ? full : first || employee?.lastname?.trim() || '';
    },
    [employeeList]
  );
  // The dialog shows overlap and absence 409s itself (#2119 D4).
  const createShift = useCreateShift({ inlineConflicts: true, nameOf: firstNameOf });
  const checkConflicts = useCheckShiftConflicts();
  const { data: templatesData } = useShiftTemplates();
  const [recentTemplateIds, setRecentTemplateIds] = useState<string[]>(() => readRecentTemplateIds());
  // Template chips (#2038): one click applies a template, the last used come first.
  const templates = useMemo(
    () => orderTemplatesByRecentUse((templatesData?.templates ?? []).filter((t) => t.id), recentTemplateIds),
    [templatesData?.templates, recentTemplateIds]
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  // Group-lead write scope (#2043): a lead picks among their groups; a lead of one group
  // gets it preselected.
  const writeScope = useShiftWriteScope();
  const allowedGroupIds = writeScope.unrestricted ? undefined : writeScope.groupIds;
  const leadDefaultGroupId = allowedGroupIds?.length === 1 ? allowedGroupIds[0] : undefined;
  // "Save and add another" (#2038) keeps the dialog open after the save.
  const addAnotherRef = useRef(false);

  // Form setup with Zod validation
  const schema = useMemo(() => getCreateShiftSchema(i18n), [i18n]);
  const form = useForm<CreateShiftFormInput, unknown, CreateShiftFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      employeeIds: defaultEmployeeId ? [defaultEmployeeId] : [],
      shiftDate: defaultDate || new Date(),
      startTime: '08:00',
      endTime: '16:00',
      breakMinutes: 40,
      breakStart: '12:00',
      breakEnd: '12:40',
      shiftType: ShiftType.REGULAR,
      groupIds: [],
      notes: '',
      recurrenceType: RecurrenceType.NONE,
      repetitions: undefined,
      weekdays: [],
    },
  });
  // The colliding days a save answered (409); cleared when the input changes (#2119 D4).
  const [saveCollision, setSaveCollision] = useState<ShiftCollision | null>(null);
  // The colliding (person, days) pairs a multi-employee save answered (#2121).
  const [savePeople, setSavePeople] = useState<PersonCollision[] | null>(null);

  // Watch fields for conflict checking
  // Several people (#2121) or one; the single-person paths read employeeId, which is empty
  // while several people are chosen and OPEN_SHIFT_VALUE for an open shift.
  const watchedEmployeeIds = useWatch({ control: form.control, name: 'employeeIds' });
  const employeeIds = useMemo(() => watchedEmployeeIds ?? [], [watchedEmployeeIds]);
  const several = employeeIds.length > 1 && employeeIds[0] !== OPEN_SHIFT_VALUE;
  const employeeId = several ? '' : (employeeIds[0] ?? '');
  const shiftDate = useWatch({ control: form.control, name: 'shiftDate' });
  const startTime = useWatch({ control: form.control, name: 'startTime' });
  const endTime = useWatch({ control: form.control, name: 'endTime' });
  // The break feeds the day and week warnings (#2042).
  const breakMinutes = useWatch({ control: form.control, name: 'breakMinutes' });

  // Watch fields for break time auto-calculation
  const breakStart = useWatch({ control: form.control, name: 'breakStart' });
  const breakEnd = useWatch({ control: form.control, name: 'breakEnd' });

  // Auto-calculate break minutes from break start/end
  useEffect(() => {
    if (breakStart && breakEnd && breakStart !== '' && breakEnd !== '' && breakEnd > breakStart) {
      const [startH, startM] = breakStart.split(':').map(Number);
      const [endH, endM] = breakEnd.split(':').map(Number);
      const minutes = (endH * 60 + endM) - (startH * 60 + startM);
      if (minutes > 0) {
        form.setValue('breakMinutes', minutes);
      }
    }
  }, [breakStart, breakEnd, form]);

  // Watch fields for recurrence
  const recurrenceType = useWatch({ control: form.control, name: 'recurrenceType' });
  const repetitions = useWatch({ control: form.control, name: 'repetitions' });
  const weekdays = useWatch({ control: form.control, name: 'weekdays' });
  // Groups currently being planned; scopes the employee dropdown soft filter (#350).
  const plannedGroupIds = useWatch({ control: form.control, name: 'groupIds' });

  // Apply a saved shift template's time/break/type fields into the form.
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    setRecentTemplateIds(rememberTemplateUse(templateId));
    if (template.start_time) form.setValue('startTime', template.start_time);
    if (template.end_time) form.setValue('endTime', template.end_time);
    form.setValue('breakStart', template.break_start ?? '');
    form.setValue('breakEnd', template.break_end ?? '');
    // When the template carries a break window, the break auto-calc effect derives
    // breakMinutes from it -- so only set it explicitly for windowless templates,
    // otherwise this set is immediately overwritten (dead) by the effect.
    const hasBreakWindow = Boolean(template.break_start) && Boolean(template.break_end);
    if (!hasBreakWindow && template.break_minutes !== undefined) {
      form.setValue('breakMinutes', template.break_minutes);
    }
    if (template.shift_type) {
      form.setValue('shiftType', template.shift_type as CreateShiftFormData['shiftType']);
    }
  };

  // Every day of the series, expanded like the backend does (#2119): the end date, the
  // number of shifts and the inline check of every day.
  const expandedDates = useMemo(
    () =>
      shiftDate
        ? expandRecurrenceDates(shiftDate, recurrenceType ?? RecurrenceType.NONE, repetitions, weekdays ?? [])
        : [],
    [shiftDate, recurrenceType, repetitions, weekdays]
  );
  const isSeries = recurrenceType !== RecurrenceType.NONE && (repetitions ?? 0) >= 1;
  const calculatedEndDate =
    isSeries && expandedDates.length > 0 ? expandedDates[expandedDates.length - 1] : null;

  // Debounce values for conflict check
  const debouncedIdsKey = useDebounce(employeeIds.join(','), 300);
  const debouncedIds = useMemo(() => (debouncedIdsKey ? debouncedIdsKey.split(',') : []), [debouncedIdsKey]);
  const debouncedSeveral = debouncedIds.length > 1 && debouncedIds[0] !== OPEN_SHIFT_VALUE;
  const debouncedEmployeeId = debouncedSeveral ? '' : (debouncedIds[0] ?? '');
  const debouncedShiftDate = useDebounce(shiftDate, 300);
  const debouncedStartTime = useDebounce(startTime, 300);
  const debouncedEndTime = useDebounce(endTime, 300);
  const debouncedBreakMinutes = useDebounce(breakMinutes, 300);
  const debouncedRecurrenceType = useDebounce(recurrenceType, 300);
  const debouncedRepetitions = useDebounce(repetitions, 300);
  const debouncedWeekdays = useDebounce(weekdays, 300);

  // Check for conflicts when all required fields are filled
  useEffect(() => {
    // An open shift (#2040) has no employee to overlap.
    const canCheckConflicts =
      (debouncedEmployeeId || debouncedSeveral) &&
      debouncedShiftDate &&
      debouncedStartTime &&
      debouncedEndTime &&
      // Validate time format (HH:MM)
      /^([01]\d|2[0-3]):([0-5]\d)$/.test(debouncedStartTime) &&
      /^([01]\d|2[0-3]):([0-5]\d)$/.test(debouncedEndTime) &&
      // End time must be after start time
      debouncedEndTime > debouncedStartTime;

    if (canCheckConflicts) {
      checkConflicts.mutate({
        // An open shift (#2040) is checked as open: no overlap, the day limit only.
        // Several people are checked one by one on the server (#2121).
        ...(debouncedSeveral
          ? { employee_ids: debouncedIds }
          : debouncedEmployeeId === OPEN_SHIFT_VALUE
            ? { open_shift: true }
            : { employee_id: debouncedEmployeeId }),
        shift_date: format(debouncedShiftDate, 'yyyy-MM-dd'),
        start_time: debouncedStartTime,
        end_time: debouncedEndTime,
        break_minutes:
          typeof debouncedBreakMinutes === 'number' && debouncedBreakMinutes >= 0
            ? debouncedBreakMinutes
            : undefined,
        // A series is checked on every day, with the save's own test (#2119 D4).
        ...(debouncedEmployeeId !== OPEN_SHIFT_VALUE &&
        debouncedRecurrenceType &&
        debouncedRecurrenceType !== RecurrenceType.NONE &&
        typeof debouncedRepetitions === 'number' &&
        debouncedRepetitions >= 1 &&
        debouncedRepetitions <= 365 &&
        (debouncedRecurrenceType !== RecurrenceType.CUSTOM_WEEKDAYS || (debouncedWeekdays?.length ?? 0) > 0)
          ? {
              recurrence_type: toEnumMember(shifts_CheckConflictsRequest.recurrence_type, debouncedRecurrenceType),
              repetitions: debouncedRepetitions,
              weekdays:
                debouncedRecurrenceType === RecurrenceType.CUSTOM_WEEKDAYS ? debouncedWeekdays : undefined,
            }
          : {}),
      });
    } else {
      // Clear stale conflict state when inputs are invalid
      checkConflicts.reset();
    }
    // Note: checkConflicts is intentionally excluded from deps to prevent infinite loop
    // The mutation object changes reference after each call, which would cause re-execution
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedIdsKey,
    debouncedShiftDate,
    debouncedStartTime,
    debouncedEndTime,
    debouncedBreakMinutes,
    debouncedRecurrenceType,
    debouncedRepetitions,
    debouncedWeekdays,
  ]);

  // A change of what is planned makes the last save's collision stale.
  useEffect(() => {
    setSaveCollision(null);
    setSavePeople(null);
  }, [employeeIds, shiftDate, startTime, endTime, recurrenceType, repetitions, weekdays]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        employeeIds: defaultEmployeeId ? [defaultEmployeeId] : [],
        shiftDate: defaultDate || new Date(),
        startTime: '08:00',
        endTime: '16:00',
        breakMinutes: 40,
        breakStart: '12:00',
        breakEnd: '12:40',
        shiftType: ShiftType.REGULAR,
        groupIds: leadDefaultGroupId ? [leadDefaultGroupId] : [],
        notes: '',
        recurrenceType: RecurrenceType.NONE,
        repetitions: undefined,
        weekdays: [],
      });
      setSelectedTemplateId('');
      setSaveCollision(null);
      setSavePeople(null);
      checkConflicts.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDate, defaultEmployeeId, leadDefaultGroupId]);

  // Handle form submission. "Save and add another" keeps the dialog open with the same
  // employee, date, times and groups, so the next shift needs one change (#2038).
  // A failed save keeps the dialog open with the input (#2119 D4): a collision is shown with
  // its days, every other failure keeps its toast.
  const saveShift = async (data: CreateShiftFormData, skipConflicts: boolean) => {
    const addAnother = addAnotherRef.current;
    addAnotherRef.current = false;
    try {
      const request = toCreateShiftRequest(data);
      await createShift.mutateAsync(skipConflicts ? { ...request, skip_conflicts: true } : request);
    } catch (error) {
      const people = peopleCollisionsFromError(error);
      if (people) {
        setSavePeople(people);
      } else {
        setSaveCollision(collisionFromError(error, format(data.shiftDate, 'yyyy-MM-dd')));
      }
      return;
    }
    if (!addAnother) {
      onOpenChange(false);
      return;
    }
    form.reset({ ...form.getValues(), notes: '', recurrenceType: RecurrenceType.NONE, repetitions: undefined, weekdays: [] });
    setSaveCollision(null);
    setSavePeople(null);
    checkConflicts.reset();
  };

  const handleSubmit = (data: CreateShiftFormData) => saveShift(data, false);

  const handleSkipConflicts = () => {
    void form.handleSubmit((data) => saveShift(data, true))();
  };

  // Enter in any text field of the form submits it (#2038). Shift+Enter keeps a newline in
  // the notes. Popovers (employee search, pickers) render outside the form element, so their
  // own Enter handling is untouched.
  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    const target = event.target as HTMLElement;
    if (!event.currentTarget.contains(target)) return;
    if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') return;
    event.preventDefault();
    void form.handleSubmit(handleSubmit)();
  };

  // Check if conflicts exist
  const hasConflicts = checkConflicts.data?.has_conflict === true;
  // The days the series collides on: the save's answer, else the inline check (#2119 D4).
  const inlineCollision: ShiftCollision | null =
    isSeries && checkConflicts.data
      ? {
          overlapDates: checkConflicts.data.conflict_dates ?? [],
          absentDates: checkConflicts.data.absent_dates ?? [],
        }
      : null;
  const collision = hasCollision(saveCollision) ? saveCollision : hasCollision(inlineCollision) ? inlineCollision : null;

  // Several people (#2121): the check per person drives the chip markers, the collision list
  // and the named warning lines.
  const perPerson = several ? checkConflicts.data?.employees : undefined;
  const inlinePeople: PersonCollision[] = (perPerson ?? [])
    .filter((p) => (p.conflict_dates?.length ?? 0) + (p.absent_dates?.length ?? 0) > 0)
    .map((p) => ({ employeeId: p.employee_id, overlapDates: p.conflict_dates ?? [], absentDates: p.absent_dates ?? [], days: [] }));
  const peopleCollisions = savePeople ?? (inlinePeople.length > 0 ? inlinePeople : null);
  const markers: Record<string, EmployeeMarker | undefined> = {};
  for (const p of perPerson ?? []) {
    const name = firstNameOf(p.employee_id);
    if (p.conflict_dates?.length || p.has_conflict) {
      markers[p.employee_id] = {
        kind: 'overlap',
        detail: i18n._({
          id: 'shifts.collision.overlapDays',
          message: '{name} already has a shift at this time on {days}.',
          values: { name, days: formatDayList(p.conflict_dates ?? [], i18n.locale) },
        }),
      };
    } else if (p.absent_dates?.length) {
      markers[p.employee_id] = {
        kind: 'absent',
        detail: i18n._({
          id: 'shifts.collision.absentDays',
          message: '{name} is absent (approved) on {days}.',
          values: { name, days: formatDayList(p.absent_dates, i18n.locale) },
        }),
      };
    } else if (p.availability) {
      markers[p.employee_id] = { kind: 'availability', detail: availabilityLine(p.availability, name, i18n) };
    }
  }
  const namedWarnings: NamedShiftWarnings[] | undefined = perPerson
    ?.filter((p) => !p.has_conflict && (p.warnings?.length ?? 0) > 0)
    .map((p) => ({ name: firstNameOf(p.employee_id), warnings: p.warnings }));
  // Each person gets a shift or a series; an open shift is one.
  const peopleCount = employeeId === OPEN_SHIFT_VALUE ? 1 : Math.max(employeeIds.length, 1);
  // The employee's first name for the availability line (#2041).
  const checkedEmployeeName = useMemo(() => {
    const employee = employeeList?.find((e) => e.id === debouncedEmployeeId);
    return employee?.firstname?.trim() || employee?.lastname?.trim() || '';
  }, [employeeList, debouncedEmployeeId]);

  // Check for employee absences on the selected date
  const absenceParams = useMemo(() => {
    if (!debouncedEmployeeId || debouncedEmployeeId === OPEN_SHIFT_VALUE || !debouncedShiftDate) return null;
    const dateStr = format(debouncedShiftDate, 'yyyy-MM-dd');
    return {
      employeeId: debouncedEmployeeId,
      fromDate: dateStr,
      toDate: dateStr,
    };
  }, [debouncedEmployeeId, debouncedShiftDate]);

  const { data: absenceData } = useEmployeeAbsences(absenceParams || {});

  // An approved or requested absence of the employee on that day (#2038). An approved one
  // makes the save fail with EMPLOYEE_ABSENT; a requested one may still be approved.
  // Rejected and cancelled absences are no clash.
  const absenceConflict = useMemo(() => {
    if (!absenceParams || !absenceData?.absences?.length) return null;
    const relevant = absenceData.absences.filter(
      (a) => a.approval_status === ApprovalStatus.APPROVED || a.approval_status === ApprovalStatus.REQUESTED
    );
    return (
      relevant.find((a) => a.approval_status === ApprovalStatus.APPROVED) ?? relevant[0] ?? null
    );
  }, [absenceParams, absenceData?.absences]);

  const absenceLine = useMemo(() => {
    if (!absenceConflict || !absenceParams) return '';
    const employee = employeeList?.find((e) => e.id === absenceParams.employeeId);
    const name = employee?.firstname?.trim() || employee?.lastname?.trim() || '';
    const values = {
      name,
      date: format(debouncedShiftDate, 'dd.MM.'),
      type: getAbsenceTypeLabel(absenceConflict.absence_type as AbsenceTypeValue, i18n),
    };
    return absenceConflict.approval_status === ApprovalStatus.APPROVED
      ? i18n._({ id: 'shifts.create.absenceApproved', message: '{name} is absent on {date} ({type}, approved)', values })
      : i18n._({ id: 'shifts.create.absenceRequested', message: '{name} is absent on {date} ({type}, requested)', values });
  }, [absenceConflict, absenceParams, employeeList, debouncedShiftDate, i18n]);

  const hasAbsenceConflict = absenceConflict !== null;
  // A single shift already shows its overlap and its absence in their own lines.
  const showCollisionAlert =
    !!collision &&
    (isSeries ||
      (collision.overlapDates.length > 0 && !hasConflicts) ||
      (collision.absentDates.length > 0 && !hasAbsenceConflict));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" onOpenAutoFocus={focusFirstShiftField}>
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'shifts.create.title', message: 'Create Shift' })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {i18n._({
              id: 'shifts.create.description',
              message: 'Create a new shift for an employee',
            })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            onKeyDown={handleFormKeyDown}
            className="space-y-4"
          >
            {/* Template chips (#2038): one-click presets, last used first */}
            {templates.length > 0 && (
              <div className="space-y-1.5" data-testid="template-chips">
                <span className="text-sm font-medium" id="template-chips-label">
                  {i18n._({ id: 'shifts.form.template', message: 'Template' })}
                </span>
                <div role="group" aria-labelledby="template-chips-label" className="flex flex-wrap gap-2">
                  {templates.map((t) => (
                    <Button
                      key={t.id}
                      type="button"
                      size="sm"
                      variant={selectedTemplateId === t.id ? 'default' : 'outline'}
                      aria-pressed={selectedTemplateId === t.id}
                      className={cn('h-8 rounded-full')}
                      onClick={() => handleTemplateSelect(t.id!)}
                    >
                      {t.name}
                      {t.start_time && t.end_time && (
                        <span className="ml-1.5 text-xs opacity-75">
                          {t.start_time}–{t.end_time}
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Colliding (person, days) pairs of several people (#2121) */}
            {several && peopleCollisions && (
              <PeopleCollisionAlert
                people={peopleCollisions}
                nameOf={firstNameOf}
                totalShifts={employeeIds.length * expandedDates.length}
                onSkip={handleSkipConflicts}
                isPending={createShift.isPending}
              />
            )}

            {/* Colliding days of a series, or the save's 409 (#2119 D4) */}
            {!several && collision && showCollisionAlert && (
              <ShiftCollisionAlert
                collision={collision}
                employeeName={checkedEmployeeName}
                totalDays={isSeries ? expandedDates.length : 1}
                onSkip={isSeries ? handleSkipConflicts : undefined}
                isPending={createShift.isPending}
              />
            )}

            {/* Conflict Warning */}
            {hasConflicts && !isSeries && !several && (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {i18n._({
                    id: 'shifts.conflict.warning',
                    message: 'Conflict Detected',
                  })}
                </AlertTitle>
                <AlertDescription>
                  {i18n._({
                    id: 'shifts.conflict.message',
                    message: 'This shift overlaps with an existing shift',
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* Availability (#2041): a warning only, the save stays enabled */}
            <ShiftAvailabilityWarning
              availability={checkConflicts.data?.availability}
              employeeName={checkedEmployeeName}
            />

            {/* Absence Conflict Warning */}
            {hasAbsenceConflict && absenceConflict && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {i18n._({
                    id: 'shifts.warning.employeeAbsence',
                    message: 'Employee Has Absence',
                  })}
                </AlertTitle>
                <AlertDescription data-testid="absence-line">{absenceLine}</AlertDescription>
              </Alert>
            )}

            {/* Working time warnings (#2042): advice only, save stays enabled. Hidden on an
                overlap: that save is refused, and its sums would count both shifts. */}
            {several ? (
              <ShiftWarningLines warnings={undefined} named={namedWarnings} />
            ) : (
              <ShiftWarningLines warnings={hasConflicts || collision ? undefined : checkConflicts.data?.warnings} />
            )}

            {/* Employee Select */}
            <FormField
              control={form.control}
              name="employeeIds"
              render={({ field, fieldState }) => (
                <FormItem>
                  <div className="flex items-center gap-1">
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.employee', message: 'Employee' })}
                    </FormLabel>
                    <HelpTip
                      data-testid="open-shift-help"
                      content={i18n._({
                        id: 'shifts.form.openShiftHelp',
                        message:
                          'Choose Open shift when nobody is assigned yet. After you publish it, the employees of its groups can ask for it in the app; the group lead or an admin approves one of them.',
                      })}
                    />
                  </div>
                  <FormControl>
                    <EmployeeChipSelect
                      value={field.value ?? []}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                      id="employee-select"
                      aria-label={i18n._({ id: 'shifts.form.employee', message: 'Employee' })}
                      aria-describedby="employee-error"
                      groupIds={plannedGroupIds}
                      allowOpenShift
                      markers={markers}
                    />
                  </FormControl>
                  {several && (
                    <p className="text-xs text-muted-foreground" data-testid="several-people-caption">
                      {i18n._({
                        id: 'shifts.form.severalPeopleCaption',
                        message: 'Each person gets their own shift; group, time and break apply to all.',
                      })}
                    </p>
                  )}
                  {field.value?.[0] === OPEN_SHIFT_VALUE && (
                    <p className="text-xs text-muted-foreground" data-testid="open-shift-caption">
                      {i18n._({
                        id: 'shifts.form.openShiftCaption',
                        message:
                          'Nobody is assigned. Choose at least one group below: its employees see the shift under Open shifts once it is published. Open shifts are single shifts, without recurrence.',
                      })}
                    </p>
                  )}
                  <FormMessage id="employee-error" />
                </FormItem>
              )}
            />

            {/* Date Picker */}
            <FormField
              control={form.control}
              name="shiftDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.date', message: 'Date' })}
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={i18n._({
                        id: 'common.selectDate',
                        message: 'Select date',
                      })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Time Fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.startTime', message: 'Start Time' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.endTime', message: 'End Time' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Break Minutes */}
            <FormField
              control={form.control}
              name="breakMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.breakMinutes', message: 'Break (min)' })}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Break Times */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="breakStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.breakStart', message: 'Break Start' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="breakEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.breakEnd', message: 'Break End' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Shift Type */}
            <FormField
              control={form.control}
              name="shiftType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.shiftType', message: 'Shift Type' })}
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={ShiftType.REGULAR}>
                        <div className="flex items-center gap-2">
                          <ShiftTypeBadge type={ShiftType.REGULAR} />
                        </div>
                      </SelectItem>
                      <SelectItem value={ShiftType.OVERTIME}>
                        <div className="flex items-center gap-2">
                          <ShiftTypeBadge type={ShiftType.OVERTIME} />
                        </div>
                      </SelectItem>
                      <SelectItem value={ShiftType.ON_CALL}>
                        <div className="flex items-center gap-2">
                          <ShiftTypeBadge type={ShiftType.ON_CALL} />
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Recurrence Type */}
            <FormField
              control={form.control}
              name="recurrenceType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.recurrence', message: 'Recurrence' })}
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={RecurrenceType.NONE}>
                        {i18n._({ id: 'shifts.form.recurrenceNone', message: 'None' })}
                      </SelectItem>
                      <SelectItem value={RecurrenceType.DAILY}>
                        {i18n._({ id: 'shifts.form.recurrenceDaily', message: 'Daily (all days)' })}
                      </SelectItem>
                      <SelectItem value={RecurrenceType.WEEKDAYS}>
                        {i18n._({ id: 'shifts.form.recurrenceWeekdays', message: 'Weekdays (Mon-Fri)' })}
                      </SelectItem>
                      <SelectItem value={RecurrenceType.WEEKLY}>
                        {i18n._({ id: 'shifts.form.recurrenceWeekly', message: 'Weekly' })}
                      </SelectItem>
                      <SelectItem value={RecurrenceType.CUSTOM_WEEKDAYS}>
                        {i18n._({ id: 'shifts.form.recurrenceCustomWeekdays', message: 'Custom weekdays' })}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Weekday picker - only shown for custom-weekday recurrence */}
            {recurrenceType === RecurrenceType.CUSTOM_WEEKDAYS && (
              <FormField
                control={form.control}
                name="weekdays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.weekdaysLabel', message: 'Weekdays' })}
                    </FormLabel>
                    <FormControl>
                      <WeekdayMultiSelect
                        value={field.value ?? []}
                        onChange={field.onChange}
                        id="weekdays-select"
                        aria-describedby="weekdays-error"
                      />
                    </FormControl>
                    <FormMessage id="weekdays-error" />
                  </FormItem>
                )}
              />
            )}

            {/* Repetitions - only shown when recurrence is not NONE */}
            {recurrenceType !== RecurrenceType.NONE && (
              <FormField
                control={form.control}
                name="repetitions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.repetitions', message: 'Repetitions' })}
                    </FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={recurrenceType === RecurrenceType.WEEKLY ? 52 : 365}  // 365 for DAILY and WEEKDAYS
                          {...field}
                          // Controlled from the first render (#2119 F10): undefined is an empty field.
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || undefined)}
                          className="w-24"
                        />
                      </FormControl>
                      <span className="text-sm text-muted-foreground">
                        {recurrenceType === RecurrenceType.WEEKLY
                          ? i18n._({ id: 'shifts.form.repetitionsWeeks', message: 'weeks' })
                          : recurrenceType === RecurrenceType.WEEKDAYS
                            ? i18n._({ id: 'shifts.form.repetitionsWeekdayShifts', message: 'weekday shifts' })
                            : recurrenceType === RecurrenceType.CUSTOM_WEEKDAYS
                              ? i18n._({ id: 'shifts.form.repetitionsShifts', message: 'shifts' })
                              : i18n._({ id: 'shifts.form.repetitionsDays', message: 'days' })}
                      </span>
                    </div>
                    <FormMessage />
                    {calculatedEndDate && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {i18n._({
                          id: 'shifts.form.shiftsUntil',
                          message: 'Shifts will be created until: {date}',
                          values: { date: formatDateDisplay(calculatedEndDate, i18n.locale) },
                        })}
                      </p>
                    )}
                  </FormItem>
                )}
              />
            )}

            {/* Groups Multi-Select */}
            <FormField
              control={form.control}
              name="groupIds"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-1.5">
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.groups', message: 'Groups' })}
                    </FormLabel>
                    <HelpTip
                      label={i18n._({ id: 'shifts.form.groupsHelpLabel', message: 'What the groups mean' })}
                      content={i18n._({
                        id: 'shifts.form.groupsHelp',
                        message:
                          'The groups the employee works in during this shift. The shift counts towards the staffing of each selected group. Without a group it counts for none.',
                      })}
                    />
                  </div>
                  <FormControl>
                    {/* `groupIds` carries a `.default([])`, so the INPUT value the
                        form holds is optional until validation fills it in. */}
                    <GroupMultiSelect
                      value={field.value ?? []}
                      onChange={field.onChange}
                      id="groups-select"
                      allowedGroupIds={allowedGroupIds}
                    />
                  </FormControl>
                  {several && (
                    <p className="text-xs text-muted-foreground" data-testid="groups-for-all-caption">
                      {i18n._({ id: 'shifts.form.groupsForAll', message: 'Applies to all selected people' })}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.notes', message: 'Notes' })}
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} maxLength={500} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* What happens on save (#2038 UX pass): a new shift is a draft until published */}
            <p className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground" data-testid="create-draft-note">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {employeeId === OPEN_SHIFT_VALUE
                ? i18n._({
                    id: 'shifts.create.draftNoteOpen',
                    message:
                      'The open shift is saved as a draft that only planners see. After you publish the period, the employees of its groups see it under Open shifts and can ask for it.',
                  })
                : i18n._({
                    id: 'shifts.create.draftNote',
                    message:
                      'The shift is saved as a draft that only planners see. The employee sees it in the app after you publish the period.',
                  })}
            </p>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {i18n._({ id: 'common.cancel', message: 'Cancel' })}
              </Button>
              <Button
                type="submit"
                variant="outline"
                disabled={createShift.isPending}
                onClick={() => {
                  addAnotherRef.current = true;
                }}
                data-testid="create-and-add-another"
              >
                {i18n._({ id: 'shifts.create.saveAndAddAnother', message: 'Save and add another' })}
              </Button>
              <Button type="submit" disabled={createShift.isPending}>
                {createShift.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {isSeries
                  ? i18n._({
                      id: 'shifts.create.submitSeries',
                      message: '{count, plural, one {Create # series} other {Create # series}}',
                      values: { count: peopleCount },
                    })
                  : i18n._({
                      id: 'shifts.create.submitShifts',
                      message: '{count, plural, one {Create # shift} other {Create # shifts}}',
                      values: { count: peopleCount },
                    })}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
